import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface Notification {
  id: number;
  title: string;
  body: string;
  // Kept loose on purpose: this union has drifted from the backend's
  // NOTIFICATION_TYPES (which sends task_assigned / task_completed / system),
  // and narrowing it would reject rows the API already returns.
  notification_type:
    | 'mention'
    | 'comment'
    | 'task'
    | 'job'
    | 'general'
    | 'system'
    | 'task_assigned'
    | 'task_completed'
    | 'dev_update'
    | (string & {});
  is_read: boolean;
  related_task_id?: number;
  related_job_id?: number;
  related_comment_id?: number;
  related_url?: string;
  created_at: string;
  created_by_name?: string;
}

export const useNotifications = (limit?: number, enabled = true) => {
  return useQuery({
    queryKey: ['notifications', limit],
    enabled,
    refetchInterval: enabled ? 30000 : false,
    queryFn: async () => {
      const params = limit ? `?limit=${limit}` : '';
      const { data } = await api.get<Notification[] | NotificationPage>(`/api/notifications/${params}`);
      return Array.isArray(data) ? data : data.results;
    },
  });
};

interface NotificationPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: Notification[];
}

export const useNotificationPage = (page: number, unreadOnly: boolean, type: string) => useQuery({
  queryKey: ['notifications', 'page', page, unreadOnly, type],
  queryFn: async (): Promise<NotificationPage> => {
    const params = new URLSearchParams({ page: String(page) });
    if (unreadOnly) params.set('read', 'false');
    if (type !== 'all') params.set('notification_type', type);
    const response = await api.get<NotificationPage | Notification[]>(`/api/notifications/?${params}`);
    const data = response.data;
    return Array.isArray(data) ? { count: data.length, next: null, previous: null, results: data } : data;
  },
  refetchInterval: 30000,
});

export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const { data } = await api.get<{ count: number }>('/api/notifications/unread_count/');
      return data.count;
    },
    refetchInterval: 30000, // Poll every 30 seconds
  });
};

export const useNotificationMutations = () => {
  const queryClient = useQueryClient();

  const markAsRead = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await api.post(`/api/notifications/${notificationId}/mark_read/`, {});
      return response.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/notifications/mark_all_read/', {});
      return response.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const deleteNotification = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await api.delete(`/api/notifications/${notificationId}/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return {
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
};